import React, { createContext, useState, useMemo, useContext } from "react";
import { ThemeProvider as MuiThemeProvider } from "@mui/material/styles";
import { lightTheme, darkTheme } from "../config/materialTheme";

// Create theme context
const ThemeContext = createContext({
  mode: "light",
  toggleTheme: () => {},
});

// Create a custom hook to use the theme context
export const useThemeContext = () => useContext(ThemeContext);

// Create ThemeProvider component
export const ThemeProvider = ({ children }) => {
  // Get initial theme from localStorage or default to light
  const [mode, setMode] = useState(
    () => localStorage.getItem("theme") || "light"
  );

  // Update body class when mode changes
  React.useEffect(() => {
    document.body.className = mode;
  }, [mode]);

  // Function to toggle theme
  const toggleTheme = () => {
    setMode((prevMode) => {
      const newMode = prevMode === "light" ? "dark" : "light";
      localStorage.setItem("theme", newMode); // Save to localStorage
      return newMode;
    });
  };

  // Memoize the context value
  const themeContextValue = useMemo(
    () => ({
      mode,
      toggleTheme,
    }),
    [mode]
  );

  // Select theme based on mode
  const theme = mode === "light" ? lightTheme : darkTheme;

  return (
    <ThemeContext.Provider value={themeContextValue}>
      <MuiThemeProvider theme={theme}>{children}</MuiThemeProvider>
    </ThemeContext.Provider>
  );
};

export default ThemeProvider;
